/* Clients — list, create, edit companies. Click a client to see their pipeline. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Trash2, Pencil, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const PIPE_STAGES = [
  { key: "draft", label: "Draft", color: "bg-slate-500" },
  { key: "ready", label: "Ready", color: "bg-sky-500" },
  { key: "submitted", label: "Submitted", color: "bg-indigo-500" },
  { key: "interview", label: "Interview", color: "bg-violet-500" },
  { key: "offer", label: "Offer", color: "bg-orange-500" },
  { key: "placed", label: "Placed", color: "bg-emerald-500" },
  { key: "hold", label: "Hold", color: "bg-amber-500" },
  { key: "rejected", label: "Rejected", color: "bg-rose-500" },
];

export default function Clients() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null); // client pipeline data
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const refresh = async () => {
    const r = await api.get("/clients");
    setList(r.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const openPipeline = async (c) => {
    try {
      const r = await api.get(`/clients/${c.id}/pipeline`);
      setActive(r.data);
    } catch { toast.error("Failed to load pipeline"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this client? Resumes linked to it will be unlinked but not deleted.")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Client deleted");
    if (active?.client?.id === id) setActive(null);
    refresh();
  };

  if (active) {
    return <ClientPipelineView data={active} onBack={() => setActive(null)} navigate={navigate} />;
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><Building2 className="size-3.5" /> Clients</div>
          <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Client companies</h1>
          <p className="text-muted-foreground mt-1 text-sm">Track the companies you submit candidates to — with per-client pipelines.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-2 lift" data-testid="clients-new-button"><Plus className="size-4" /> New client</Button>
      </div>

      {list.length === 0 ? (
        <Card className="border-dashed border-border">
          <CardContent className="p-10 text-center">
            <Building2 className="size-12 text-muted-foreground mx-auto opacity-50" />
            <div className="display font-bold text-lg mt-3">No clients yet</div>
            <div className="text-sm text-muted-foreground">Add your first client to track submissions and pipelines per company.</div>
            <Button className="mt-4 gap-2" onClick={() => setCreating(true)}><Plus className="size-4" /> Add first client</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => (
            <Card key={c.id} className="border-border hover:shadow-md transition-shadow" data-testid={`client-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <button onClick={() => openPipeline(c)} className="text-left flex-1 min-w-0" data-testid={`client-open-${c.id}`}>
                    <div className="display font-bold text-lg truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.industry || "—"}</div>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => setEditing(c)} data-testid={`client-edit-${c.id}`}><Pencil className="size-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(c.id)} className="text-destructive" data-testid={`client-del-${c.id}`}><Trash2 className="size-4" /></Button>
                  </div>
                </div>
                {c.contact_name && <div className="text-xs text-muted-foreground mt-2">{c.contact_name} · {c.contact_email}</div>}
                {c.notes && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.notes}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <ClientDialog initial={editing || {}} onClose={() => { setCreating(false); setEditing(null); refresh(); }} />
      )}
    </div>
  );
}

function ClientPipelineView({ data, onBack, navigate }) {
  const { client, pipeline, total_resumes, submissions_count } = data;
  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <Button variant="ghost" onClick={onBack} className="gap-2 mb-4" data-testid="client-back"><ArrowLeft className="size-4" /> Back to clients</Button>

      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><Building2 className="size-3.5" /> Client Pipeline</div>
          <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">{client.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            {client.industry && <span>{client.industry}</span>}
            {client.contact_email && <span> · {client.contact_email}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Candidates · Submissions</div>
          <div className="display text-2xl font-black" data-testid="client-totals">{total_resumes} · {submissions_count}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-4">
        {PIPE_STAGES.map((s) => {
          const count = (pipeline?.[s.key] || []).length;
          return (
            <div key={s.key} className="rounded-lg border border-border p-3" data-testid={`client-stage-${s.key}`}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground flex items-center gap-1.5"><span className={`size-1.5 rounded-full ${s.color}`} />{s.label}</div>
              <div className="display text-2xl font-extrabold mt-1">{count}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {PIPE_STAGES.filter((s) => (pipeline?.[s.key] || []).length > 0).map((s) => (
          <Card key={s.key} className="border-border">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground flex items-center gap-1.5 mb-2"><span className={`size-1.5 rounded-full ${s.color}`} />{s.label} · {(pipeline?.[s.key] || []).length}</div>
              <div className="space-y-1.5">
                {(pipeline?.[s.key] || []).map((r) => (
                  <button key={r.id} onClick={() => navigate(`/editor/${r.id}`)} className="w-full text-left flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded-md hover:bg-secondary" data-testid={`client-resume-${r.id}`}>
                    <span className="truncate font-semibold">{r.data?.name || r.title}</span>
                    <Send className="size-3 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
        {Object.keys(pipeline || {}).length === 0 && (
          <Card className="border-dashed border-border md:col-span-2 xl:col-span-4">
            <CardContent className="p-10 text-center text-sm text-muted-foreground">
              No resumes linked to this client yet. Open any resume in the Editor and set its Client to <strong>{client.name}</strong>.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function ClientDialog({ initial, onClose }) {
  const [p, setP] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!p.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = {
        name: p.name, industry: p.industry, website: p.website,
        contact_name: p.contact_name, contact_email: p.contact_email, contact_phone: p.contact_phone,
        notes: p.notes, default_from_email: p.default_from_email, default_cc: p.default_cc,
      };
      if (p.id) await api.put(`/clients/${p.id}`, body);
      else await api.post("/clients", body);
      toast.success("Saved");
      onClose();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[540px]" data-testid="client-dialog">
        <DialogHeader><DialogTitle>{p.id ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Company name *" value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="client-name" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Industry" value={p.industry || ""} onChange={(e) => setP({ ...p, industry: e.target.value })} />
            <Input placeholder="Website" value={p.website || ""} onChange={(e) => setP({ ...p, website: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Contact name" value={p.contact_name || ""} onChange={(e) => setP({ ...p, contact_name: e.target.value })} />
            <Input placeholder="Contact email" value={p.contact_email || ""} onChange={(e) => setP({ ...p, contact_email: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Contact phone" value={p.contact_phone || ""} onChange={(e) => setP({ ...p, contact_phone: e.target.value })} />
            <Input placeholder="Default CC (optional)" value={p.default_cc || ""} onChange={(e) => setP({ ...p, default_cc: e.target.value })} />
          </div>
          <Textarea rows={3} placeholder="Notes" value={p.notes || ""} onChange={(e) => setP({ ...p, notes: e.target.value })} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="client-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
